import React, { useState } from 'react';

const FEEDBACK_DEEPLINK = 'https://t.me/H2O_nsk_bot?start=feedback';
const API_BASE = process.env.REACT_APP_API_URL || '/api';

/**
 * Блок «предложки» на экране завершения мойки.
 *
 * mode="link" — короткая строка со ссылкой-диплинком на бота (для Telegram mini-app).
 * mode="form" — мини-форма прямо в приложении: текст уходит на /api/feedback,
 *               бэкенд пересылает его в служебную группу (без ухода в Telegram).
 */
const FeedbackPrompt = ({
  mode = 'form',
  source = 'app',
  deepLink = FEEDBACK_DEEPLINK,
  collectContact = false, // гость: показываем «Имя» + «Телефон/e-mail» (у нас нет его контактов)
  requireConsent = false, // гость: обязательная галка согласия на обработку ПДн
}) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | sending | done | error

  const wrapStyle = {
    marginTop: 16,
    padding: '16px 18px',
    background: 'var(--h2o-surface, #141d33)',
    border: '1px solid var(--h2o-border, rgba(255,255,255,0.08))',
    borderRadius: 'var(--h2o-r-lg, 20px)',
  };
  const titleStyle = { margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--h2o-text, #eaf1fb)' };
  const subStyle = { margin: '6px 0 0', fontSize: 13, color: 'var(--h2o-text-muted, #93a3bd)' };
  const linkStyle = { color: 'var(--h2o-brand, #22c8e6)', fontWeight: 700, textDecoration: 'none', cursor: 'pointer' };

  if (mode === 'link') {
    return (
      <div style={wrapStyle}>
        <p style={titleStyle}>💬 Есть что предложить?</p>
        <p style={subStyle}>
          Расскажите, что улучшить —{' '}
          <a href={deepLink} target="_blank" rel="noopener noreferrer" style={linkStyle}>
            напишите сюда
          </a>
          . Читаем всё.
        </p>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div style={wrapStyle}>
        <p style={titleStyle}>Спасибо! 🙏</p>
        <p style={subStyle}>Ваш отзыв отправлен команде H2O. Мы читаем всё и становимся лучше.</p>
      </div>
    );
  }

  const canSubmit =
    message.trim().length >= 2 &&
    status !== 'sending' &&
    (!collectContact || (name.trim() && contact.trim())) &&
    (!requireConsent || consent);

  const submit = async () => {
    if (!canSubmit) return;
    setStatus('sending');
    try {
      const res = await fetch(`${API_BASE}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message.trim(),
          name: name.trim(),
          contact: contact.trim(),
          consent,
          source,
        }),
      });
      if (!res.ok) throw new Error('bad status');
      setStatus('done');
    } catch (e) {
      setStatus('error');
    }
  };

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    background: 'var(--h2o-surface-2, #1a2440)',
    border: '1px solid var(--h2o-border, rgba(255,255,255,0.08))',
    borderRadius: 'var(--h2o-r-md, 14px)',
    color: 'var(--h2o-text, #eaf1fb)',
    padding: '12px 14px',
    fontSize: 14,
    fontFamily: 'inherit',
    outline: 'none',
  };

  if (!open) {
    return (
      <div style={wrapStyle}>
        <p style={titleStyle}>💬 Есть что предложить?</p>
        <p style={subStyle}>
          Замечание или идея —{' '}
          <span style={linkStyle} onClick={() => setOpen(true)} role="button" tabIndex={0}>
            написать нам
          </span>
          . Не выходя из приложения.
        </p>
      </div>
    );
  }

  return (
    <div style={wrapStyle}>
      <p style={titleStyle}>💬 Ваше предложение</p>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Что улучшить? Что понравилось, а что нет?"
        rows={3}
        maxLength={2000}
        style={{ ...inputStyle, marginTop: 10, resize: 'vertical', minHeight: 72 }}
      />
      {collectContact && (
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ваше имя"
          maxLength={100}
          style={{ ...inputStyle, marginTop: 8 }}
        />
      )}
      <input
        value={contact}
        onChange={(e) => setContact(e.target.value)}
        placeholder={collectContact ? 'Телефон или e-mail для ответа' : 'Как с вами связаться? Телефон/@ник (по желанию)'}
        maxLength={200}
        style={{ ...inputStyle, marginTop: 8 }}
      />
      {requireConsent && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 12, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            style={{ marginTop: 2, width: 18, height: 18, flexShrink: 0, accentColor: 'var(--h2o-brand, #22c8e6)' }}
          />
          <span style={{ fontSize: 12.5, color: 'var(--h2o-text-muted, #93a3bd)', lineHeight: 1.45 }}>
            Согласен на обработку персональных данных в соответствии с{' '}
            <a href="/oferta.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              условиями
            </a>
            .
          </span>
        </label>
      )}
      {status === 'error' && (
        <p style={{ ...subStyle, color: 'var(--h2o-danger, #ff6b6b)' }}>
          Не удалось отправить. Попробуйте ещё раз чуть позже.
        </p>
      )}
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          style={{
            flex: 1,
            padding: '12px 16px',
            fontSize: 14,
            fontWeight: 700,
            color: 'var(--h2o-brand-fg, #04121a)',
            background: 'var(--h2o-gradient-brand, linear-gradient(135deg, #22c8e6, #5b8cff))',
            border: 'none',
            borderRadius: 'var(--h2o-r-pill, 999px)',
            cursor: canSubmit ? 'pointer' : 'default',
            opacity: canSubmit ? 1 : 0.55,
          }}
        >
          {status === 'sending' ? 'Отправляем…' : 'Отправить'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            padding: '12px 16px',
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--h2o-text-muted, #93a3bd)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Отмена
        </button>
      </div>
    </div>
  );
};

export default FeedbackPrompt;
